/* global Node, Element, Document */
export async function getEmailContent(): Promise<string> {
  return new Promise((resolve, reject) => {
    const item = Office.context.mailbox.item;

    if (!item) {
      reject(new Error("Kein E-Mail-Element verfügbar"));
      return;
    }

    item.body.getAsync(Office.CoercionType.Text, (asyncResult) => {
      if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
        resolve(asyncResult.value);
      } else {
        reject(
          new Error(asyncResult.error?.message || "E-Mail-Inhalt konnte nicht abgerufen werden")
        );
      }
    });
  });
}

export function splitEmailBody(body: string) {
  const marker = /(Von:|From:|On .* wrote:)/i;
  const parts = body.split(marker);
  if (parts.length < 3) {
    return {
      primary: body,
      context: "",
    };
  }

  const primary = (marker.exec(body)?.[0] || "") + parts[2];
  const context = parts.slice(3).join("");
  return { primary, context };
}

export function extractUserDraft(body: string): string {
  const marker = /(Von:|From:|On .* wrote:)/i;
  const parts = body.split(marker);

  if (parts.length < 2) {
    return body.trim();
  }
  const userDraft = parts[0];
  return userDraft.trim();
}
export function extractPreviewData(primary: string) {
  let text = primary.replace(/\r?\n/g, " ").trim();
  let from = "Unbekannt";
  const fromMatch = text.match(/Von:\s*(.*?)(<|$)/i);
  if (fromMatch && fromMatch[1]) {
    from = fromMatch[1].trim();
  }

  let subject = "";
  let bodyText = "";

  const subjectMatch = text.match(/Betreff:\s*(.*)/i);
  if (subjectMatch && subjectMatch[1]) {
    subject = subjectMatch[1].trim();
    subject = subject.replace(/^(AW:|RE:|FW:|WG:)\s*/i, "").trim();
    subject = subject.replace(/\u00A0/g, " ");

    const match = subject.match(/^(.*?)\s{2,}(.*)$/);
    if (match) {
      subject = match[1].trim();
      bodyText = match[2].trim();
    }
  }

  const maxSubjectLength = 100;
  const truncatedSubject =
    subject.length > maxSubjectLength ? subject.substring(0, maxSubjectLength) + "..." : subject;

  const maxBodyLength = 50;
  const snippet =
    bodyText.length > maxBodyLength ? bodyText.slice(0, maxBodyLength) + "..." : bodyText;

  return { from, truncatedSubject, snippet };
}

const REPLY_CHAIN_SELECTORS = [
  "#divRplyFwdMsg",
  "[id^='x_divRplyFwdMsg']",
  "#appendonsend",
  "hr#stopSpelling",
];

function isBefore(a: Node, b: Node): boolean {
  return !!(a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING);
}

function liftToBlockStart(el: Element, root: HTMLElement): Element {
  let current = el;
  while (current.parentElement && current.parentElement !== root) {
    const range = el.ownerDocument.createRange();
    range.setStart(current.parentElement, 0);
    range.setEndBefore(current);
    if (range.toString().trim() !== "") break;
    current = current.parentElement;
  }
  return current;
}

export function findReplyChainStart(doc: Document): Element | null {
  const candidates: Element[] = [];

  REPLY_CHAIN_SELECTORS.forEach((sel) => {
    const el = doc.querySelector(sel);
    if (el) candidates.push(el);
  });

  const mailOriginal =
    doc.querySelector('a[name="_MailOriginal"]') ?? doc.querySelector('[style*="_MailOriginal"]');
  if (mailOriginal) candidates.push(mailOriginal);

  const classicHeader = Array.from(doc.body.querySelectorAll<HTMLElement>("div[style]")).find((d) =>
    /border-top:\s*solid\s+#(E1E1E1|B5C4DF)/i.test(d.getAttribute("style") ?? "")
  );
  if (classicHeader) candidates.push(classicHeader);

  const hr = doc.body.querySelector("hr");
  if (hr) candidates.push(hr);

  if (candidates.length === 0) return null;

  const earliest = candidates.sort((a, b) => (isBefore(a, b) ? -1 : 1))[0];
  return liftToBlockStart(earliest, doc.body);
}

function findSignatureStart(doc: Document, chainStart: Element | null): Element | null {
  const candidates = [
    doc.getElementById("Signature"),
    doc.querySelector('[id*="signature" i]'),
    doc.querySelector('[class*="signature" i]'),
    doc.querySelector('a[name="_MailAutoSig"]')?.closest("p, div") ?? null, // classic Outlook
  ];
  return candidates.find((c) => c && (!chainStart || isBefore(c, chainStart))) ?? null;
}

function removeEverythingBefore(node: Node, root: Node) {
  let current: Node | null = node;
  while (current && current !== root) {
    while (current.previousSibling) current.parentNode!.removeChild(current.previousSibling);
    current = current.parentNode;
  }
}

function removeFromHereOn(node: Node, root: Node) {
  let current: Node | null = node;
  while (current && current !== root) {
    while (current.nextSibling) current.parentNode!.removeChild(current.nextSibling);
    const parent = current.parentNode;
    if (current === node) parent?.removeChild(current);
    current = parent;
  }
}

function getBodyHtml(): Promise<string> {
  const body = Office.context.mailbox.item.body as any;
  return new Promise((resolve, reject) =>
    body.getAsync(
      Office.CoercionType.Html,
      { bodyMode: Office.MailboxEnums.BodyMode.HostConfig },
      (result: any) =>
        result.status === Office.AsyncResultStatus.Succeeded
          ? resolve(result.value)
          : reject(result.error)
    )
  );
}

export function extractUserDraftFromHtml(html: string): string {
  const doc = new DOMParser().parseFromString(html, "text/html");
  const chainStart = findReplyChainStart(doc);
  const signatureStart = findSignatureStart(doc, chainStart);
  const cutAt = signatureStart ?? chainStart;
  if (cutAt) removeFromHereOn(cutAt, doc.body);

  return doc.body.innerText?.trim() ?? "";
}

export async function replaceEntireBody(newText: string): Promise<void> {
  const body = Office.context.mailbox.item.body as any;
  const html = await getBodyHtml();
  const doc = new DOMParser().parseFromString(html, "text/html");

  const chainStart = findReplyChainStart(doc);
  const signatureStart = findSignatureStart(doc, chainStart);
  if (!signatureStart) {
    console.warn("[KARLI] Signature not found above history", { hasHistory: !!chainStart });
  }
  const keepFrom = signatureStart ?? chainStart;
  const replyNode = doc.createElement("div");
  replyNode.innerHTML = newText.replace(/\n/g, "<br/>");

  if (keepFrom) {
    removeEverythingBefore(keepFrom, doc.body);
    keepFrom.parentNode!.insertBefore(replyNode, keepFrom);
    if (!signatureStart) keepFrom.parentNode!.insertBefore(doc.createElement("br"), keepFrom);
  } else {
    doc.body.innerHTML = "";
    doc.body.appendChild(replyNode);
  }

  const outHtml = chainStart ? doc.documentElement.outerHTML : doc.body.innerHTML;
  await new Promise<void>((resolve, reject) => {
    body.setAsync(
      outHtml,
      {
        coercionType: Office.CoercionType.Html,
        bodyMode: Office.MailboxEnums.BodyMode.HostConfig,
      },
      (result: any) => {
        if (result.status === Office.AsyncResultStatus.Failed) {
          console.error("[KARLI] Error replacing body:", result.error?.message);
          reject(result.error);
        } else {
          resolve();
        }
      }
    );
  });
}
